import { FC, useEffect, useState } from 'react';
import { IStudent, IStudentClassInfo } from '../../../models/students.model';
import moment from 'moment';
import { Table, TableColumnsType } from 'antd';

import StudentName from './Name';
import Button from '../../Button';
import Pagination from '../../Pagination';
import Loader from '../../Loader';
import config from '../../../lib/config';
import CounselForm from './CounselForm';
import useAxios from '../../../hooks/useAxios';
import {
  ICounsel,
  ICounselMenuResponse,
  IGetCounsels,
} from '../../../models/studentsCounsel.model';
import {
  getStudentCounselMenu,
  getStudentCounsels,
} from '../../../api/studentsCounsel.api';
import ErrorResponse from '../../ErrorResponse';
import { getCommonCodeLabel } from '../../../lib/store';
import useAuth from '../../../hooks/useAuth';
import { useTranslation } from 'react-i18next';

interface IProps {
  student: IStudent | undefined;
  classInfo: IStudentClassInfo[]|undefined;
}

const StudentCounsel: FC<IProps> = ({
  student,
  classInfo,
}) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<ICounselMenuResponse>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetCounsels>();

  const [isEdit, setIsEdit] = useState(false);
  const [page, setPage] = useState(1);
  const [formMode, setFormMode] = useState('create');
  const [counsel, setCounsel] = useState<ICounsel>();

  useEffect(() => {
    execute(getStudentCounselMenu());
  }, []);

  useEffect(() => {
    if (student) doGetStudentCounsels();
  }, [student]);

  const doGetStudentCounsels = () => {
    if (!student) return;
    execute2(getStudentCounsels(student.studentCode, student.campusCode));
  };

  const columns: TableColumnsType<ICounsel> = [
    {
      title: t('student.student.column31'),
      dataIndex: 'counselDate',
      key: 'counselDate',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.counselDate?.localeCompare(b.counselDate),
      render: (value, record) =>
        `${moment(value).format('YYYY.MM.DD')} ${record.counselTime}`,
    },
    {
      title: t('student.student.column32'),
      dataIndex: 'counselTargetGBN',
      key: 'counselTargetGBN',
      align: 'center',
      width: '5%',
      sorter: (a, b) => a.counselTargetGBN?.localeCompare(b.counselTargetGBN),
      render: (value: string) => getCommonCodeLabel('MB0004', value, 'ko'),
    },
    {
      title: t('student.student.column33'),
      dataIndex: 'counselChannelCode',
      key: 'counselChannelCode',
      align: 'center',
      width: '5%',
      sorter: (a, b) =>
        a.counselChannelCode?.localeCompare(b.counselChannelCode),
      render: (value: string) => getCommonCodeLabel('MB0007', value, 'ko'),
    },
    {
      title: t('student.student.column143'),
      dataIndex: 'counselGBN',
      key: 'counselGBN',
      align: 'center',
      width: '6%',
      sorter: (a, b) => a.counselGBN?.localeCompare(b.counselGBN),
      render: (value: string) => getCommonCodeLabel('MB0008', value, 'ko'),
    },
    {
      title: t('common.column.title'),
      dataIndex: 'counselTitle',
      key: 'counselTitle',
      width: '10%',
      align: 'center',
      ellipsis: true,
      sorter: (a, b) => a.counselTitle?.localeCompare(b.counselTitle),
      render: (value) => <div className="text-left ant-table-cell-ellipsis">{value}</div>
    },
    {
      title: t('common.column.content'),
      dataIndex: 'counselContent',
      key: 'counselContent',
      width: '30%',
      align: 'center',
      ellipsis: true,
      sorter: (a, b) => a.counselTitle?.localeCompare(b.counselContent),
      render: (value) => <div className="text-left ant-table-cell-ellipsis">{value}</div>
    },
    {
      title: t('student.student.column35'),
      dataIndex: 'writerMemberName',
      key: 'writerMemberName',
      align: 'center',
      width: '6%',
      sorter: (a, b) => a.writerMemberName?.localeCompare(b.writerMemberName),
    },
  ];

  return (
    <>
      {(isLoading || isLoading2) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />

      <StudentName
        student={student}
        classInfo={classInfo}
      />
      {isEdit ? (
        <CounselForm
          formMode={formMode}
          setFormMode={setFormMode}
          counsel={counsel}
          setCounsel={setCounsel}
          student={student}
          menus={res?.data ?? []}
          setIsEdit={setIsEdit}
          doGetStudentCounsels={doGetStudentCounsels}
        />
      ) : (
        <>
          <div>
            <Table
              className='comm-table__area'
              columns={columns}
              dataSource={res2?.data}
              pagination={{
                current: page,
                pageSize: config.smallPageSize,
              }}
              onRow={(record) => {
                return {
                  onClick: () => {
                    setCounsel(record);
                    setFormMode('read');
                    setIsEdit(true);
                  },
                };
              }}
              rowClassName="pointer"
            />
          </div>
          {/* studentCounsels */}
          <div className="comm-bx__between">
            <div />
            <div className="common-paging center">
              <Pagination
                count={res2?.data?.length || 0}
                page={page}
                perPage={config.smallPageSize}
                setPage={setPage}
              />
            </div>
            <div className="comm-btn__area right">
              <Button
                className="btn-basic pink-line"
                onClick={() => {
                  setFormMode('create');
                  setIsEdit(true);
                }}
                text={t('common.button.create')}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default StudentCounsel;
